import React from 'react';
import Hero from '../components/Hero';
import InfraHook from '../components/InfraHook';
import HowItWorks from '../components/HowItWorks';
import LiveActivity from '../components/LiveActivity';
import Partners from '../components/Partners';
import CollaborateSection from '../components/CollaborateSection';
import WorksWithStrip from '../components/WorksWithStrip';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import FAQ from '../components/FAQ';
import CountUp from '../components/CountUp';
import MissionHub from '../components/MissionHub';
import TeamPreview from '../components/TeamPreview';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, FileText, Star, Lock, Zap, Calendar, Clock, TrendingUp, ShieldCheck, Server, Bot, Terminal } from 'lucide-react';

// -------------------------------------------------------------------------
// Home page section order (2026-02 pivot):
//   1. Hero (EXPLORE INFRA → /hacking-servers)
//   2. Stats row
//   3. PUBLIC OPS · TRY LIVE (InfraHook — recon in one tap)
//   4. MISSION_HUB · ONE DECISION (What do you need today?)
//   5. How It Works
//   6. THE OPS TEAM (mini preview → /info/team)
//   7. Live activity ticker
//   8. Books teaser
//   9. Memberships
//   10. 2026 Hacking Playbook (blogs → each cross-links to a paid service)
//   11. Testimonials · FAQ · CTA
// -------------------------------------------------------------------------

const STAT_COLORS = ['#00ff9d', '#4de0ff', '#c084fc', '#ffd34d'];

const parseStatValue = (s) => {
  const m = String(s).match(/[\d.]+/);
  const num = m ? parseFloat(m[0]) : 0;
  const suffix = String(s).replace(/[\d.]+/, '');
  return { num, suffix };
};

const StatsRow = () => {
  const { config } = useSiteConfig();
  const stats = config.stats || [];
  return (
    <section className="relative py-12 sm:py-16 overflow-hidden">
      {/* Ambient blobs — subtle */}
      <div aria-hidden className="pointer-events-none absolute -top-10 left-1/4 w-64 h-64 rounded-full opacity-15 blur-3xl" style={{ background: '#00ff9d' }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-10 right-1/4 w-64 h-64 rounded-full opacity-10 blur-3xl" style={{ background: '#c084fc' }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {stats.map((s, i) => {
            const { num, suffix } = parseStatValue(s.value);
            const isFloat = String(num).includes('.');
            const c = STAT_COLORS[i % STAT_COLORS.length];
            return (
              <div
                key={s.label}
                className="stat-tile relative rounded-2xl border-2 p-4 sm:p-6 text-center overflow-hidden"
                style={{
                  borderColor: `${c}45`,
                  background: `linear-gradient(160deg, ${c}0d 0%, transparent 65%)`,
                  '--c': c,
                }}
                data-testid={`stat-${i}`}
              >
                <span aria-hidden className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${c}, transparent)` }} />
                <div
                  className="font-black leading-none"
                  style={{ color: c, fontSize: 'clamp(1.7rem, 4.5vw, 2.8rem)', fontFamily: 'Cinzel, serif', textShadow: `0 0 22px ${c}55` }}
                >
                  {isFloat ? s.value : <CountUp end={num} suffix={suffix} />}
                </div>
                <div className="eh-mono text-[9.5px] sm:text-[10.5px] tracking-[.3em] opacity-70 mt-3">{s.label}</div>
              </div>
            );
          })}
        </div>
      </div>
      <style>{`
        .stat-tile { transition: transform .35s cubic-bezier(.2,.9,.3,1), border-color .25s, box-shadow .35s; }
        .stat-tile:hover { transform: translateY(-3px); border-color: var(--c) !important; box-shadow: 0 22px 60px -22px color-mix(in srgb, var(--c) 55%, transparent); }
      `}</style>
    </section>
  );
};

const BooksTeaser = () => {
  const { config } = useSiteConfig();
  return (
    <section className="py-16 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between mb-8 sm:mb-10 flex-wrap gap-4">
          <div>
            <div className="eh-kicker mb-3">// LIBRARY</div>
            <h2 className="eh-display font-black" style={{ fontSize: 'clamp(1.6rem, 5vw, 3.2rem)' }}>HACKING <span className="eh-neon">EBOOKS</span></h2>
          </div>
          <Link to="/books" className="eh-btn-ghost text-xs">BROWSE LIBRARY <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          {config.books.slice(0, 4).map(b => (
            <Link key={b.id} to="/books" className="eh-card eh-panel p-3 sm:p-4 group">
              <div className="relative aspect-[3/4] rounded overflow-hidden mb-3 sm:mb-4 bg-black">
                <img src={b.cover} alt={b.title} className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-500" />
                {b.tag && <span className="absolute top-2 left-2 eh-mono text-[10px] tracking-widest px-2 py-1 rounded" style={{ background: 'var(--eh-green)', color: '#001a10' }}>{b.tag}</span>}
              </div>
              <div className="text-xs sm:text-sm font-semibold leading-snug" style={{ fontFamily: 'Inter,sans-serif' }}>{b.title}</div>
              <div className="flex items-center justify-between mt-2 eh-mono text-[11px] opacity-70">
                <span className="truncate">{b.author}</span><span className="eh-neon-soft shrink-0 ml-2">${b.price}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const TIER_COLORS = ['#4de0ff', '#00ff9d', '#c084fc'];

const MembershipsBlock = () => {
  const { config } = useSiteConfig();
  const tiers = config.memberships || [];
  return (
    <section className="relative py-16 sm:py-22 overflow-hidden">
      {/* Ambient blobs */}
      <div aria-hidden className="pointer-events-none absolute -top-20 -left-16 w-72 h-72 rounded-full opacity-15 blur-3xl" style={{ background: '#4de0ff' }} />
      <div aria-hidden className="pointer-events-none absolute top-40 right-0 w-72 h-72 rounded-full opacity-15 blur-3xl" style={{ background: '#c084fc' }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-24 left-1/3 w-72 h-72 rounded-full opacity-15 blur-3xl" style={{ background: '#00ff9d' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-14">
          <div className="eh-kicker justify-center mb-3">// MEMBERSHIPS</div>
          <h2 className="eh-display font-black leading-tight" style={{ fontSize: 'clamp(1.8rem, 5.5vw, 3.4rem)' }}>
            JOIN THE <span className="eh-neon">UNDERGROUND</span>
          </h2>
          <p className="opacity-75 mt-4 max-w-lg mx-auto text-sm leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
            Pick a tier. Cancel anytime. Encrypted payments only.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {tiers.map((m, i) => {
            const c = TIER_COLORS[i % TIER_COLORS.length];
            const isPop = !!m.popular;
            return (
              <div
                key={m.id}
                className="tier-tile relative rounded-2xl border-2 p-6 sm:p-7 overflow-hidden"
                style={{
                  borderColor: isPop ? c : `${c}45`,
                  background: `linear-gradient(160deg, ${c}0f 0%, transparent 65%)`,
                  boxShadow: isPop ? `0 30px 70px -30px ${c}90` : 'none',
                  '--c': c,
                }}
                data-testid={`tier-${m.id}`}
              >
                <span aria-hidden className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${c}, transparent)` }} />

                {isPop && (
                  <span
                    className="absolute top-3 right-3 eh-mono text-[9px] tracking-widest font-black px-2 py-1 rounded-full"
                    style={{ background: c, color: '#04120a' }}
                  >
                    MOST POPULAR
                  </span>
                )}

                {/* Tier badge */}
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: `${c}1a`, border: `1px solid ${c}55`, color: c }}>
                    {m.color === 'red'   && <Zap size={16} strokeWidth={2} />}
                    {m.color === 'green' && <Star size={16} strokeWidth={2} />}
                    {m.color === 'cyan'  && <Lock size={16} strokeWidth={2} />}
                    {!['red','green','cyan'].includes(m.color) && <Star size={16} strokeWidth={2} />}
                  </div>
                  <div className="eh-mono text-[11px] font-black tracking-[.2em]" style={{ color: c }}>{m.name}</div>
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-1 mb-5">
                  <span
                    className="font-black leading-none"
                    style={{ color: c, fontSize: 'clamp(2.3rem, 6vw, 3.2rem)', fontFamily: 'Cinzel, serif', textShadow: `0 0 22px ${c}55` }}
                  >
                    ${m.price}
                  </span>
                  <span className="eh-mono text-[11px] opacity-60">/{m.period}</span>
                </div>

                {/* Perks */}
                <ul className="space-y-2 mb-6">
                  {(m.perks || []).map(p => (
                    <li key={p} className="flex items-start gap-2 text-[13px] leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
                      <Check size={14} className="mt-0.5 shrink-0" style={{ color: c }} strokeWidth={2.5} />
                      <span className="opacity-90">{p}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/memberships"
                  className="w-full flex items-center justify-center gap-1.5 py-3 rounded-lg font-black text-[11px] tracking-widest transition-all hover:brightness-110"
                  style={{ background: c, color: '#04120a', boxShadow: `0 8px 24px -10px ${c}80` }}
                  data-testid={`tier-cta-${m.id}`}
                >
                  SUBSCRIBE <ArrowRight size={12} strokeWidth={2.5} />
                </Link>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .tier-tile { transition: transform .35s cubic-bezier(.2,.9,.3,1), border-color .25s, box-shadow .35s; }
        .tier-tile:hover { transform: translateY(-4px); border-color: var(--c) !important; box-shadow: 0 30px 70px -25px color-mix(in srgb, var(--c) 60%, transparent); }
      `}</style>
    </section>
  );
};

// 2026 Hacking Playbook — each post cross-links to a paid service so the
// blog section behaves as a soft-sell funnel, not dead editorial content.
const PLAYBOOK_2026 = [
  {
    id: 'ig-recovery-2026',
    tag: 'RECOVERY',
    cover: 'https://images.unsplash.com/photo-1611262588024-d12430b98920?w=800&auto=format&fit=crop&q=60',
    title: 'Instagram Account Locked in 2026? The 7-Step Playbook That Works.',
    excerpt: 'Meta rolled out AI-driven security holds in Q1 2026. Here\'s the exact appeal template + operator route that recovers 82% of them.',
    color: '#00ff9d',
    date: 'Feb 12 · 2026',
    read: '7 min',
    icon: ShieldCheck,
    cta: 'START RECOVERY',
    to: '/advisory',
    cross: 'Recovery Ops',
  },
  {
    id: 'osint-username-2026',
    tag: 'OSINT',
    cover: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&auto=format&fit=crop&q=60',
    title: 'One Username, 47 Platforms: The 2026 OSINT Sweep Every SOC Needs',
    excerpt: 'From Bluesky to Nostr, our updated Sherlock-lite scan hits 47 platforms in parallel. Blueprint + open-source code.',
    color: '#4de0ff',
    date: 'Feb 09 · 2026',
    read: '11 min',
    icon: Terminal,
    cta: 'RUN LIVE SCAN',
    to: '/hacking-servers',
    cross: 'Hacking Infra Hub',
  },
  {
    id: 'ai-scam-2026',
    tag: 'AI · SCAM',
    cover: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&auto=format&fit=crop&q=60',
    title: 'Deepfake Voice Scams Tripled in Q1 2026 — Here\'s How to Spot Them',
    excerpt: 'Real audio samples + the acoustic fingerprints that give AI voices away. Companion tool: 60-sec voice-scam scanner.',
    color: '#ff2d92',
    date: 'Feb 05 · 2026',
    read: '9 min',
    icon: Bot,
    cta: 'SCAN A CLIP',
    to: '/tools/security',
    cross: 'AI Security Suite',
  },
  {
    id: 'ig-safety-2026',
    tag: 'DEFENCE',
    cover: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=800&auto=format&fit=crop&q=60',
    title: 'Instagram Account Hardening 2026: The 5 Settings That Block 90% Of Takeovers',
    excerpt: 'Session control, backup codes, login alerts, trusted contacts, and the one setting Meta hid three menus deep. Free 10-minute audit checklist.',
    color: '#ffd34d',
    date: 'Feb 02 · 2026',
    read: '8 min',
    icon: TrendingUp,
    cta: 'BOOK CONSULTATION',
    to: '/advisory',
    cross: 'Cyber-Incident Consultation',
  },
  {
    id: 'cve-log4shell-still-2026',
    tag: 'CVE',
    cover: 'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=800&auto=format&fit=crop&q=60',
    title: 'The Top 12 CVEs Still Actively Exploited in 2026',
    excerpt: 'log4shell isn\'t going anywhere. Neither is CVE-2024-4577. Fresh NVD data + one-click detection playbooks.',
    color: '#ff8a4d',
    date: 'Jan 28 · 2026',
    read: '13 min',
    icon: Server,
    cta: 'SCAN A DOMAIN',
    to: '/hacking-servers',
    cross: 'Hacking Infra Hub',
  },
  {
    id: 'password-2026',
    tag: 'PRIVACY',
    cover: 'https://images.unsplash.com/photo-1633265486064-086b219458ec?w=800&auto=format&fit=crop&q=60',
    title: 'Passkeys > Passwords in 2026 — Migration Guide for 8 Big Platforms',
    excerpt: 'FIDO2 adoption crossed 60% this year. What breaks, what to keep as backup, and how to audit your whole vault in an hour.',
    color: '#c084fc',
    date: 'Jan 22 · 2026',
    read: '10 min',
    icon: Lock,
    cta: 'CHECK BREACHES',
    to: '/tools/breach',
    cross: 'Breach Checker',
  },
];

const BlogsTeaser = () => (
  <section className="py-16 sm:py-22 relative overflow-hidden">
    <div className="absolute inset-0 pointer-events-none opacity-60" style={{
      background: 'radial-gradient(50% 60% at 20% 20%, rgba(0,255,157,.05), transparent 70%), radial-gradient(40% 50% at 85% 80%, rgba(255,45,146,.05), transparent 70%)',
    }} />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
      <div className="text-center mb-10 sm:mb-14">
        <div className="eh-kicker justify-center mb-3">// 2026 HACKING PLAYBOOK</div>
        <h2 className="eh-display font-black leading-tight" style={{ fontSize: 'clamp(1.7rem, 5vw, 3.2rem)' }}>
          FIELD-TESTED <span className="eh-neon">TACTICS</span>. FRESH FROM 2026.
        </h2>
        <p className="opacity-70 mt-3 max-w-xl mx-auto text-sm" style={{ fontFamily: 'Inter,sans-serif' }}>
          Every article ends with a live tool or paid op you can trigger in one tap.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {PLAYBOOK_2026.map((p) => {
          const Icon = p.icon;
          return (
            <Link
              key={p.id}
              to={p.to}
              data-testid={`playbook-card-${p.id}`}
              className="playbook-card group relative rounded-2xl overflow-hidden border-2 flex flex-col"
              style={{ borderColor: `${p.color}45`, background: `linear-gradient(180deg, ${p.color}08, rgba(0,0,0,.75))`, '--c': p.color }}
            >
              <div className="relative aspect-video overflow-hidden">
                <img src={p.cover} alt={p.title} className="w-full h-full object-cover opacity-85 group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${p.color}08, rgba(0,0,0,.65))` }} />
                <span className="absolute top-3 left-3 eh-mono text-[10px] tracking-widest px-2.5 py-1 rounded-full flex items-center gap-1 font-bold" style={{ background: p.color, color: '#0a0703' }}>
                  <FileText size={10} /> {p.tag}
                </span>
                <div className="absolute top-3 right-3 eh-mono text-[10px] tracking-widest px-2 py-1 rounded-full border" style={{ borderColor: `${p.color}55`, background: 'rgba(0,0,0,.65)', color: p.color }}>
                  <Clock size={9} className="inline mr-1" />{p.read}
                </div>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="text-base font-bold leading-snug mb-2 group-hover:text-white transition-colors" style={{ fontFamily: 'Inter,sans-serif' }}>
                  {p.title}
                </div>
                <div className="text-[13px] opacity-70 leading-relaxed mb-4 line-clamp-2">
                  {p.excerpt}
                </div>
                <div className="mt-auto pt-3 flex items-center justify-between border-t" style={{ borderColor: `${p.color}22` }}>
                  <div className="eh-mono text-[10px] opacity-60 flex items-center gap-1"><Calendar size={10} /> {p.date}</div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full eh-mono text-[10px] tracking-widest font-bold group-hover:brightness-110 transition-all" style={{ background: p.color, color: '#0a0703' }}>
                    <Icon size={10} /> {p.cta} <ArrowRight size={9} />
                  </div>
                </div>
                <div className="mt-2 eh-mono text-[9.5px] tracking-widest opacity-50">
                  → CROSS-LINK · {p.cross.toUpperCase()}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
    <style>{`
      .playbook-card { transition: transform .35s cubic-bezier(.2,.9,.3,1), border-color .25s, box-shadow .4s; }
      .playbook-card:hover { transform: translateY(-4px); border-color: var(--c) !important; box-shadow: 0 20px 60px -22px color-mix(in srgb, var(--c) 55%, transparent); }
    `}</style>
  </section>
);

const CTA = () => (
  <section className="relative py-16 sm:py-22 overflow-hidden">
    {/* Ambient multi-color glow */}
    <div aria-hidden className="pointer-events-none absolute -top-16 left-1/4 w-80 h-80 rounded-full opacity-20 blur-3xl" style={{ background: '#00ff9d' }} />
    <div aria-hidden className="pointer-events-none absolute -bottom-16 right-1/4 w-80 h-80 rounded-full opacity-18 blur-3xl" style={{ background: '#ff2d92' }} />
    <div aria-hidden className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full opacity-12 blur-3xl" style={{ background: '#4de0ff' }} />

    <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
      <div
        className="relative rounded-3xl border-2 p-8 sm:p-14 text-center overflow-hidden"
        style={{
          borderColor: 'rgba(0,255,157,0.4)',
          background: 'linear-gradient(160deg, rgba(0,255,157,0.10) 0%, rgba(77,224,255,0.05) 40%, rgba(192,132,252,0.06) 100%)',
          boxShadow: '0 40px 100px -40px rgba(0,255,157,0.35)',
        }}
        data-testid="home-cta"
      >
        {/* Multi-color accent border top */}
        <span aria-hidden className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: 'linear-gradient(90deg, transparent, #00ff9d, #4de0ff, #c084fc, #ff2d92, transparent)' }} />
        {/* Corner tickers */}
        <span aria-hidden className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 rounded-tl-lg" style={{ borderColor: '#00ff9d' }} />
        <span aria-hidden className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 rounded-tr-lg" style={{ borderColor: '#4de0ff' }} />
        <span aria-hidden className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 rounded-bl-lg" style={{ borderColor: '#c084fc' }} />
        <span aria-hidden className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 rounded-br-lg" style={{ borderColor: '#ff2d92' }} />

        <div className="relative">
          <div className="eh-kicker justify-center mb-3">// READY_OPERATOR?</div>
          <h2
            className="font-black leading-tight mb-4"
            style={{ fontSize: 'clamp(1.9rem, 6.5vw, 4rem)', fontFamily: 'Cinzel, serif' }}
          >
            RUN YOUR FIRST{' '}
            <span
              style={{
                background: 'linear-gradient(90deg, #00ff9d, #4de0ff, #c084fc)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 18px rgba(0,255,157,.4))',
              }}
            >
              OP TODAY
            </span>
          </h2>
          <p className="opacity-80 max-w-xl mx-auto text-sm sm:text-base mb-8 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
            From free recon to full pentest engagements. Encrypted from the first byte.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/hacking-servers"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-black text-[11px] tracking-widest transition-all hover:brightness-110 hover:-translate-y-0.5"
              style={{ background: '#00ff9d', color: '#04120a', boxShadow: '0 12px 32px -10px rgba(0,255,157,0.75)' }}
              data-testid="home-cta-infra"
            >
              EXPLORE INFRA <ArrowRight size={14} strokeWidth={2.5} />
            </Link>
            <Link
              to="/memberships"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-[11px] tracking-widest border-2 transition-all hover:-translate-y-0.5"
              style={{ borderColor: 'rgba(77,224,255,.5)', color: '#4de0ff' }}
              data-testid="home-cta-memberships"
            >
              VIEW MEMBERSHIPS
            </Link>
          </div>

          {/* Trust chips row */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            {[
              { label: '5,800+ SERVICES', c: '#00ff9d' },
              { label: 'REFILL WARRANTY', c: '#4de0ff' },
              { label: 'ENCRYPTED · AES-256', c: '#c084fc' },
              { label: 'AUTHORIZED ONLY', c: '#ffd34d' },
            ].map(chip => (
              <span
                key={chip.label}
                className="eh-mono text-[9.5px] tracking-widest px-2.5 py-1 rounded-full border"
                style={{ borderColor: `${chip.c}55`, background: `${chip.c}0d`, color: chip.c }}
              >
                {chip.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <StatsRow />
    <InfraHook />
    <MissionHub />
    <WorksWithStrip />
    <CollaborateSection />
    <Partners />
    <HowItWorks />
    <TeamPreview />
    <LiveActivity />
    <BooksTeaser />
    <MembershipsBlock />
    <BlogsTeaser />
    <TestimonialsCarousel />
    <FAQ limit={5} />
    <CTA />
  </>
);
export default Home;
